import { useEffect, useState, useCallback } from "react";
import { API, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { FileSpreadsheet, FileText, Calendar, Printer } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "sale", label: "Sales Report", dated: true },
  { key: "purchase", label: "Purchase Report", dated: true },
  { key: "stock", label: "Stock Report", dated: false },
  { key: "expiry", label: "Expiry Report", dated: false },
  { key: "credit", label: "Credit / Outstanding", dated: false },
  { key: "vendor_ledger", label: "Vendor Ledger", dated: true, vendor: true },
  { key: "cash_book", label: "Cash Book", dated: true },
  { key: "income", label: "Income Report", dated: true },
  { key: "expenditure", label: "Expenditure Report", dated: true },
  { key: "gst", label: "GST Report (GSTR-1 / 3B)", dated: true },
];

export default function Reports() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "admin";
  const isAdmin = isPlatformAdmin || user?.role === "branch_head";
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [type, setType] = useState("sale");
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [vendorId, setVendorId] = useState("");
  const [vendors, setVendors] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const current = REPORTS.find((r) => r.key === type);

  useEffect(() => {
    API.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    if (isPlatformAdmin) {
      API.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    }
  }, [isPlatformAdmin]);

  const run = useCallback(async () => {
    if (current?.vendor && !vendorId) return; // wait for selection
    setLoading(true);
    try {
      const params = {};
      if (current?.dated) { params.date_from = from; params.date_to = to; }
      if (current?.vendor) params.vendor_id = vendorId;
      if (isAdmin && branchId) params.branch_id = branchId;
      const { data } = await API.get(`/reports/${type}`, { params });
      setReport(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [type, from, to, vendorId, current, isAdmin, branchId]);

  useEffect(() => { run(); }, [run]);

  const download = (fmt) => {
    const qs = new URLSearchParams({ format: fmt });
    if (current?.dated) { qs.set("date_from", from); qs.set("date_to", to); }
    if (current?.vendor) qs.set("vendor_id", vendorId);
    if (isAdmin && branchId) qs.set("branch_id", branchId);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${type}/export?${qs}`, "_blank");
  };

  const printReport = () => {
    const qs = new URLSearchParams({ auto: "1" });
    if (current?.dated) { qs.set("date_from", from); qs.set("date_to", to); }
    if (current?.vendor) qs.set("vendor_id", vendorId);
    if (isAdmin && branchId) qs.set("branch_id", branchId);
    window.open(`/print/report/${type}?${qs}`, "_blank", "width=1100,height=1000");
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="reports-page">
      <div>
        <div className="overline mb-2">Insights</div>
        <h1 className="font-display text-3xl font-bold">Reports</h1>
        <p className="text-sm text-slate-500 mt-1">Sales, purchases, stock, expiry, credit outstanding and vendor ledgers — exportable as PDF or Excel.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <div className="overline mb-1.5">Report Type</div>
          <select value={type} onChange={(e) => setType(e.target.value)}
                  data-testid="report-type-select"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
            {REPORTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>

        {isPlatformAdmin && (
          <div className="min-w-[180px]">
            <div className="overline mb-1.5">Branch</div>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
                    data-testid="report-branch-select"
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white w-full">
              <option value="">All branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
        )}

        {current?.vendor && (
          <div className="min-w-[220px]">
            <div className="overline mb-1.5">Vendor</div>
            <select required value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                    data-testid="report-vendor-select"
                    className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white w-full">
              <option value="">— Select vendor —</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
        )}

        {current?.dated && (
          <>
            <div>
              <div className="overline mb-1.5">From</div>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <div className="overline mb-1.5">To</div>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" className="border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </>
        )}

        <button onClick={run} className="btn-ghost" data-testid="report-run"><Calendar className="w-4 h-4" /> Run</button>
        <div className="ml-auto flex gap-2">
          <button onClick={printReport} className="btn-ghost" data-testid="report-print" disabled={current?.vendor && !vendorId}><Printer className="w-4 h-4" /> Print</button>
          <button onClick={() => download("pdf")} className="btn-ghost" data-testid="export-pdf" disabled={current?.vendor && !vendorId}><FileText className="w-4 h-4" /> PDF</button>
          <button onClick={() => download("excel")} className="btn-primary" data-testid="export-excel" disabled={current?.vendor && !vendorId}><FileSpreadsheet className="w-4 h-4" /> Excel</button>
        </div>
      </div>

      {current?.vendor && !vendorId ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-sm text-slate-500">
          Select a vendor above to view the ledger.
        </div>
      ) : report && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex justify-between flex-wrap gap-3">
            <div>
              <div className="overline">{current?.dated ? report.subtitle : "As on today"}</div>
              <h3 className="font-display text-xl font-semibold mt-1">{report.title}</h3>
            </div>
            {report.summary && (
              <div className="flex flex-wrap gap-4 text-sm">
                {Object.entries(report.summary).map(([k, v]) => (
                  <div key={k} className="text-right">
                    <div className="overline text-[10px]">{k}</div>
                    <div className="font-display font-bold text-base">{v}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {loading ? (
            <div className="p-12 text-center text-slate-500 text-sm">Generating…</div>
          ) : report.rows.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm">No data for the selected range.</div>
          ) : (
            <div className="overflow-x-auto max-h-[600px]">
              <table className="data-table">
                <thead><tr>{report.headers.map((h) => <th key={h}>{h}</th>)}</tr></thead>
                <tbody>
                  {report.rows.map((row, i) => (
                    <tr key={`${i}-${String(row[0] ?? "")}`}>
                      {row.map((c, j) => {
                        const isLinkable = report.row_ids?.[i] && (
                          (type === "sale" && report.headers[j] === "Bill No") ||
                          (type === "purchase" && report.headers[j] === "PO No")
                        );
                        if (isLinkable) {
                          const href = type === "sale"
                            ? `/invoice/${report.row_ids[i]}`
                            : `/vendor/${report.row_ids[i]}`; // purchase → vendor ledger fallback
                          return (
                            <td key={`${report.headers[j]}-${i}`}>
                              <a
                                href={href}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (type === "sale") {
                                    window.open(`/invoice/${report.row_ids[i]}`, "_blank", "width=900,height=1000");
                                  } else {
                                    // open vendor ledger in same tab
                                    window.location.href = href;
                                  }
                                }}
                                data-testid={`bill-link-${report.row_ids[i]}`}
                                className="font-mono text-xs text-[#002FA7] font-semibold hover:underline cursor-pointer"
                              >
                                {String(c)}
                              </a>
                            </td>
                          );
                        }
                        return <td key={`${report.headers[j]}-${i}`}>{String(c)}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
